import { useState } from "react";
import { Stream } from "@cloudflare/stream-react";

const FAMILY_EULOGY_ID = "684dc782509143fd749ae76ce2103016";
const FAMILY_EULOGY_POSTER_URL =
  "https://customer-n5ekme97isdmfb28.cloudflarestream.com/684dc782509143fd749ae76ce2103016/thumbnails/thumbnail.jpg?time=5s&width=1280&height=720&fit=clip";

export default function FamilyEulogy() {
  const [activeTranscript, setActiveTranscript] = useState(null);

  const toggleTranscript = (language) => {
    setActiveTranscript((current) => (current === language ? null : language));
  };

  return (
    <section className="live-funeral" aria-labelledby="family-eulogy-title">
      <div className="live-funeral__heading">
        <span className="live-funeral__cross" aria-hidden="true">✝</span>
        <p className="live-funeral__kicker">A Family Tribute</p>
        <h1 id="family-eulogy-title">Eulogy by Family</h1>
        <p>
          A loving tribute delivered by Father Paul Oneid&apos;s family during his
          funeral service in Ottawa on October 17, 2026.
        </p>
      </div>

      <section className="live-funeral__recording" aria-labelledby="family-eulogy-recording-title">
        <div className="live-funeral__section-heading">
          <p className="live-funeral__section-label">Recorded Eulogy</p>
          <h2 id="family-eulogy-recording-title">Eulogy by Family</h2>
        </div>

        <div className="live-funeral__player">
          <Stream
            controls
            src={FAMILY_EULOGY_ID}
            poster={FAMILY_EULOGY_POSTER_URL}
            responsive
            preload="metadata"
            autoplay={false}
            muted={false}
            primaryColor="#7d3f75"
            letterboxColor="#2e1c29"
            title="Eulogy by Father Paul Oneid's family"
          />
        </div>

        <div className="family-eulogy-script">
          <div className="family-eulogy-script__heading">
            <p className="live-funeral__section-label">AI-Assisted Draft Transcript</p>
            <h2>Family Eulogy Script</h2>
            <p>
              This draft follows the shortened 14-minute, 50-second recording and
              was lightly edited for readability. Family names and Lebanese place
              names should be checked against the recording before use as an
              official transcript.
            </p>
          </div>

          <div className="family-eulogy-script__transcript-controls" aria-label="Family eulogy transcript languages">
            <button
              type="button"
              className="family-eulogy-script__transcript-button"
              aria-expanded={activeTranscript === "english"}
              onClick={() => toggleTranscript("english")}
            >
              Read the family eulogy transcript
            </button>
            <button
              type="button"
              className="family-eulogy-script__transcript-button family-eulogy-script__transcript-button--arabic"
              aria-expanded={activeTranscript === "arabic"}
              onClick={() => toggleTranscript("arabic")}
              lang="ar"
              dir="rtl"
            >
              قراءة ترجمة كلمة العائلة — ترجمة بالذكاء الاصطناعي
            </button>
          </div>

          {activeTranscript === "english" && (
            <div className="family-eulogy-script__body">
              <p>
                Your Excellency, Most Reverend Bishop; the President of Lebanon,
                Joseph Aoun, represented by His Excellency the Ambassador of
                Lebanon; Monsignor; members of the clergy; fathers and sisters;
                honourable members of the Canadian Parliament; distinguished
                dignitaries and representatives of our Lebanese-Canadian
                community; dear family and friends; and Baba—that is what we, his
                children, called him, and it means “father” for those who do not
                speak Lebanese.
              </p>
              <p>
                It is my privilege and honour to deliver the eulogy for a man of
                God who himself offered more than a thousand eulogies, and to share
                with you who Dad was and who Dad is: a glimpse of a life not fully
                captured in the published record. My challenge is how to summarize
                one hundred years of love and seventy-five years of service in only
                a few minutes.
              </p>
              <p>
                Father Paul Oneid was known to the world first as a priest, and
                also as a founder of schools, principal, educator, mayor, poet,
                writer, and family man. To understand his mission, one only needs
                the words spoken by Saint Paul on the road to Damascus: “Lord,
                what would You have me do?” At his ordination he chose the name
                Paul. The Blessed Virgin Mary was his patron, and he always spoke
                with affection of Saint Anthony as his neighbour.
              </p>
              <p>
                When asked about his mission, Dad answered through his life: “I am
                the servant of God. I am the servant of the people.” Dad, I am
                certain that Saint Paul and Saint Peter have already welcomed you
                into Heaven and said, “Mission accomplished.” The words from the
                Gospel of Matthew say it too: “Well done, good and faithful
                servant. Come, share your master&apos;s joy.” He loved God, and God
                loved him. He loved all of us, and we all loved him.
              </p>
              <p>
                The story can be told in numbers: one hundred years of life;
                seventy-five years of priestly service; seventy-five years of
                marriage; eight children—four sons and four daughters;
                twenty-four grandchildren—twelve boys and twelve girls; and ten
                great-grandchildren. He died on October 13, the date remembered for
                the great Miracle of the Sun at Fatima, witnessed by tens of
                thousands. To us, these details feel larger than coincidence. We
                were blessed to be his children and to witness his life closely.
              </p>
              <p>
                Remember Father Paul Oneid—our Baba—as a man of God and a servant
                of God. As his earthly family, we witnessed the quiet character
                behind his public work: service, obedience, dedication, kindness,
                respect, holiness, and, above all, love—love of God and love of His
                people.
              </p>
              <p>
                Some images remain with us forever. We remember driving at night
                on the dark roads of Lebanon and seeing a lone soldier walking
                between villages. Even with eight or nine of us already squeezed
                into a small car, Dad would say, “Make room; we are bringing him
                with us.” My sister Laura remembers travelling with him to Tyre.
                He would stop and reach into his pockets to give money to someone
                in need. When she reminded him that they had very little, he said,
                “When you reach into your pocket for the poor, you will always find
                money.” That was our father.
              </p>
              <p>
                We are proud to be in the lineage of such a holy man. On his behalf
                we offer several thanks. First, to the clergy, to the Most Reverend
                Bishop, and to this Body of Christ—the men and women devoted to
                God. Dad&apos;s deepest joy was here, with Jesus and with all of you.
                His family never tried to compete with that joy. We thank you for
                sharing it with him.
              </p>
              <p>
                He would thank the people of every parish that allowed him the joy
                of serving: Kfour, where he began; Kfarwa and the small farms,
                sometimes only a few houses, where he would set up an altar in a
                family home and celebrate Mass; beloved Alma El Chaab; Aichieh,
                which held a place in his heart beyond comparison and where he
                wished to be laid to rest; and later Araya and the many communities
                he served after displacement. The outpouring of love from Lebanon,
                Canada, and around the world is proof of the love he gave and the
                love returned to him.
              </p>
              <p>
                He would thank our mother. Behind this great man stood a great
                woman. While he served so many people and carried so many
                responsibilities, she raised eight children and cared for the
                family. The two most important “yeses” of Dad&apos;s life were his yes
                to the Lord and his yes on his wedding day. God chose him, and he
                chose Mom wisely.
              </p>
              <p>
                To the grandchildren: you are his living legacy. You have both a
                responsibility and a gift—to continue along his path. You now have
                a grandfather in Heaven to intercede for you. When life becomes
                difficult, turn to him. And to Paul, his grandson who follows the
                path to priesthood, we remember how, when Dad first saw the twins
                Joe and Paul, he asked which one was Paul and said, “This one will
                be a priest.” Stories like this remind us that his life was part of
                something greater than ourselves.
              </p>
              <p>
                His dedication never faded. At the age of eighty he learned
                computer skills so that he could write and preserve his work. He
                authored seventeen books of wisdom, religion, history, and poetry.
                We urge everyone to help keep that legacy alive. Thanks to my
                brother George, these books and memories are preserved at
                fatherpauloneid.com. Take even a minute to look through them, and
                you will find something from Father Paul that touches you.
              </p>
              <p>
                Dad—Baba—you had the biggest heart of any man I know. In that heart
                you made room for all of us. You loved us all, and for that we love
                you.
              </p>
              <p lang="ar" dir="rtl">
                شكراً.
              </p>
            </div>
          )}

          {activeTranscript === "arabic" && (
            <div className="family-eulogy-script__body family-eulogy-script__body--arabic" lang="ar" dir="rtl">
              <p className="family-eulogy-script__ai-notice">
                ترجمة عربية أُعدّت بمساعدة الذكاء الاصطناعي، وقد تحتوي على أخطاء. يُرجى الرجوع إلى النص الإنجليزي والتسجيل المصوّر للتحقق من الدقة.
              </p>
              <p>
                صاحب السيادة المطران الجليل؛ فخامة رئيس الجمهورية اللبنانية جوزاف عون، ممثَّلاً بسعادة سفير لبنان؛ المونسنيور؛ أعضاء الإكليروس؛ الآباء والراهبات؛ حضرات أعضاء البرلمان الكندي؛ أصحاب المقامات وممثلي الجالية اللبنانية الكندية؛ أهلنا وأصدقاءنا الأعزاء؛ وبابا—وهكذا كنا نحن أولاده نناديه، وتعني كلمة «بابا» الأب لمن لا يتكلم اللهجة اللبنانية.
              </p>
              <p>
                إنه لامتياز وشرف لي أن ألقي كلمة تأبين لرجل من رجال الله، ألقى هو نفسه أكثر من ألف كلمة تأبين، وأن أشارككم من كان أبي ومن يبقى في وجداننا: لمحة عن حياة لا تستطيع السجلات المنشورة أن تختصرها كاملة. أما التحدي أمامي فهو كيف ألخّص مئة عام من المحبة وخمسة وسبعين عاماً من الخدمة في دقائق قليلة.
              </p>
              <p>
                عُرف الأب بولس عنيد في العالم أولاً ككاهن، وعُرف أيضاً مؤسساً للمدارس، ومديراً ومربّياً، ورئيساً للبلدية، وشاعراً وكاتباً وربَّ عائلة. ولكي نفهم رسالته، يكفينا أن نتذكر كلمات القديس بولس على طريق دمشق: «يا رب، ماذا تريدني أن أفعل؟» وعند سيامته اختار اسم بولس. وكانت السيدة العذراء شفيعته، وكان يتحدث دائماً بمودة عن القديس أنطونيوس بوصفه جاره.
              </p>
              <p>
                وحين كان يُسأل عن رسالته، كان أبي يجيب من خلال حياته: «أنا خادم الله. أنا خادم الناس». يا أبي، أنا على يقين بأن القديس بولس والقديس بطرس قد استقبلاك في السماء وقالا لك: «لقد أتممت الرسالة». وكلمات إنجيل متى تقول ذلك أيضاً: «نعماً أيها العبد الصالح والأمين... ادخل إلى فرح سيدك». لقد أحب الله، وأحبه الله. أحبنا جميعاً، وأحببناه جميعاً.
              </p>
              <p>
                ويمكن رواية الحكاية بالأرقام: مئة عام من الحياة؛ وخمسة وسبعون عاماً من الخدمة الكهنوتية؛ وخمسة وسبعون عاماً من الزواج؛ وثمانية أولاد—أربعة أبناء وأربع بنات؛ وأربعة وعشرون حفيداً—اثنا عشر صبياً واثنتا عشرة فتاة؛ وعشرة من أبناء الأحفاد. توفي في الثالث عشر من تشرين الأول، وهو التاريخ الذي تُستذكر فيه معجزة الشمس الكبرى في فاطيما، التي شهدها عشرات الآلاف. بالنسبة إلينا، تبدو هذه التفاصيل أكبر من أن تكون مجرد مصادفة. لقد نلنا نعمة أن نكون أولاده وأن نشهد حياته عن قرب.
              </p>
              <p>
                تذكّروا الأب بولس عنيد—بابا بالنسبة إلينا—كرجل من رجال الله وخادم لله. وبصفتنا عائلته على الأرض، شهدنا الخصال الهادئة الكامنة خلف عمله العلني: الخدمة، والطاعة، والتفاني، واللطف، والاحترام، والقداسة، وفوق كل شيء المحبة—محبة الله ومحبة شعبه.
              </p>
              <p>
                ثمة صور تبقى معنا إلى الأبد. نتذكر أننا كنا نسير بالسيارة ليلاً على طرق لبنان المظلمة، فنرى جندياً وحيداً يمشي بين القرى. ومع أننا كنا ثمانية أو تسعة أشخاص محشورين أصلاً في سيارة صغيرة، كان أبي يقول: «أفسحوا له مكاناً؛ سنأخذه معنا». وتتذكر أختي لورا سفرها معه إلى صور، إذ كان يتوقف ويمد يده إلى جيبه ليعطي محتاجاً بعض المال. وحين ذكّرته بأن ما معهما قليل، قال لها: «عندما تمدين يدك إلى جيبك من أجل الفقير، ستجدين دائماً مالاً». هكذا كان أبونا.
              </p>
              <p>
                نحن فخورون بانتمائنا إلى سلالة رجل بهذه القداسة. وباسمه نتقدم بعدة كلمات شكر. أولاً، نشكر الإكليروس، وصاحب السيادة المطران، وجسد المسيح هذا—الرجال والنساء المكرسين لله. كان فرح أبي الأعمق هنا، مع يسوع ومعكم جميعاً. ولم تحاول عائلته يوماً أن تنافس هذا الفرح. نشكركم لأنكم شاركتموه إياه.
              </p>
              <p>
                وكان ليشكر أبناء كل رعية أتاحت له فرح الخدمة: الكفور، حيث بدأ؛ وكفروة والمزارع الصغيرة، التي لم يكن بعضها يضم سوى بضعة بيوت، حيث كان ينصب مذبحاً في منزل إحدى العائلات ويحتفل بالقداس؛ وعَلما الشعب الحبيبة؛ والعيشية، التي احتلت في قلبه مكاناً لا يُضاهى، وحيث تمنى أن يُوارى الثرى؛ ثم عاريا والجماعات الكثيرة التي خدمها بعد التهجير. إن فيض المحبة من لبنان وكندا ومن أنحاء العالم دليل على المحبة التي بذلها والمحبة التي عادت إليه.
              </p>
              <p>
                وكان سيشكر والدتنا. فخلف هذا الرجل العظيم وقفت امرأة عظيمة. وبينما كان يخدم أعداداً كبيرة من الناس ويحمل مسؤوليات كثيرة، ربّت هي ثمانية أولاد واعتنت بالعائلة. وأهم كلمتي «نعم» في حياة أبي كانتا: نعم للرب، ونعم يوم زفافه. لقد اختاره الله، وهو أحسن اختيار أمي.
              </p>
              <p>
                إلى الأحفاد: أنتم إرثه الحي. أمامكم مسؤولية وهبة معاً—أن تواصلوا السير في دربه. أصبح لكم الآن جد في السماء يشفع لكم. وعندما تصعب الحياة، الجأوا إليه. وإلى بولس، حفيده الذي يسير في طريق الكهنوت، نتذكر أنه عندما رأى أبي التوأمين جو وبولس للمرة الأولى، سأل أيهما بولس، ثم قال: «هذا سيصبح كاهناً». تذكّرنا مثل هذه القصص بأن حياته كانت جزءاً من أمر أعظم منا جميعاً.
              </p>
              <p>
                ولم يَفتر تفانيه قط. ففي سن الثمانين تعلّم استخدام الكمبيوتر كي يكتب أعماله ويحفظها. وألّف سبعة عشر كتاباً في الحكمة والدين والتاريخ والشعر. ندعو الجميع إلى المساعدة في إبقاء هذا الإرث حياً. وبفضل أخي جورج، حُفظت هذه الكتب والذكريات على موقع fatherpauloneid.com. خصصوا ولو دقيقة واحدة لتصفّحها، وستجدون شيئاً من الأب بولس يلامس قلوبكم.
              </p>
              <p>
                يا أبي—يا بابا—كان لك أكبر قلب عرفته في رجل. وفي ذلك القلب أفسحت لنا جميعاً مكاناً. أحببتنا كلنا، ولذلك نحبك.
              </p>
              <p>شكراً.</p>
            </div>
          )}
        </div>
      </section>

      <div className="live-funeral__guidance">
        <h2>Watching the eulogy</h2>
        <ul>
          <li>Press the play button and turn on your sound.</li>
          <li>You may pause, resume, or move to any part of the eulogy.</li>
          <li>A stable Wi-Fi or wired internet connection is recommended.</li>
        </ul>
      </div>
    </section>
  );
}
